import { z } from "zod";

import { enumReadSchema } from "../../../shared/core/enum-read.schema";
import { VibeFailureReasonCodeReadSchema } from "../../../shared/domain/vibe/vibe-failure-reason-code";

/**
 * Emitted by the build-runner post-success composite TX when a
 * VibeBuildJob flips RUNNING -> SUCCEEDED. Persisted INSIDE the
 * same `$transaction` as the build-job UPDATE + the sibling
 * DEPLOYMENT_BUILD_SUCCEEDED audit + the deployment UPDATE so the
 * cross-row "build SUCCEEDED <=> deployment past BUILDING"
 * invariant extends to the audit feed atomically — neither audit
 * survives without the other. `actorUserId` is null (the build
 * runner is a system actor).
 *
 * `durationMs` is the canonical input to the VIBE_BUILD_MIN
 * consumption aggregation — the cost-safety rollup reads it from
 * the audit feed (or from the VibeUsageEvent ingested in the same
 * arc; both surfaces carry the same value). `logsRef` is the
 * archived S3 key for post-mortem log review (distinct from the
 * live-stream key the runner used during the build — the runner
 * moves the object to its final key as part of the succeed flow).
 */
export const VibeAuditBuildJobSucceededPayloadSchema = z.object({
  eventType: z.literal("BUILD_JOB_SUCCEEDED"),
  vibeBuildJobId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  /// Wallclock build duration in ms. Non-negative integer per the
  /// entity invariant (the entity factory throws on negative).
  /// `.finite()` is defensive against a future floating-point
  /// migration path landing in audit payloads.
  durationMs: z.number().int().nonnegative().finite(),
  /// Where the archived log object lives (an `s3://bucket/key` URI),
  /// or EMPTY when there is none.
  ///
  /// This was `.min(1)`, and the only reason that ever held is that a
  /// claim used to fabricate `s3://vibe-logs/<org>/<job>.log` — a path
  /// into a bucket no stack has ever created. The constraint was
  /// satisfied by a string, never by a file. Now that the location is
  /// recorded only once a log has actually been stored, "no stored log"
  /// is a real and common state: a build that printed nothing, or one
  /// whose archive write failed. Rejecting it here would turn that into
  /// a thrown `VibeAuditEvent.create` and lose the audit row entirely —
  /// spending a whole build's audit trail to defend a field that was
  /// lying anyway.
  logsRef: z.string()
});
export type VibeAuditBuildJobSucceededPayload = z.infer<
  typeof VibeAuditBuildJobSucceededPayloadSchema
>;

/**
 * Emitted when a build job flips RUNNING -> FAILED. The autonomous
 * build-runner writes this from its dispatch-failure compensation
 * path INSIDE the same `$transaction` as the build-job UPDATE, so
 * the audit row + the FAILED flip land atomically — neither survives
 * without the other, per
 * `feedback_swallowed_fire_and_forget_silent_failure`. The admin
 * manual escape-hatch (`MarkVibeBuildJobFailedUseCase`) does NOT emit
 * this: operator-driven transitions are captured by the admin
 * AuditAction trail instead, mirroring the SUCCEEDED slice's
 * "autonomous emits, escape-hatch stays quiet" split.
 *
 * `durationMs` is still aggregated as VIBE_BUILD_MIN consumption — a
 * build that ran for 90s then crashed consumed 90s of CPU. On the
 * dispatch-failure path no build ran, so the value is 0 (the entity
 * invariant allows 0). `errorReason` is the CLI surface text;
 * detailed traces live at `logsRef` (the archived S3 key, set at
 * claim time and non-empty for any RUNNING job).
 */
export const VibeAuditBuildJobFailedPayloadSchema = z.object({
  eventType: z.literal("BUILD_JOB_FAILED"),
  vibeBuildJobId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  /// Wallclock build duration in ms. Non-negative integer per the
  /// entity invariant (the entity factory throws on negative /
  /// non-integer). 0 on the dispatch-failure path (no build ran).
  /// `.finite()` is defensive against a future floating-point
  /// migration path landing in audit payloads.
  durationMs: z.number().int().nonnegative().finite(),
  /// Surface text shown by the CLI — the buildpack's last stderr
  /// line, or a canned infrastructure-failure message. No max length:
  /// capping here would make `VibeAuditEvent.create` throw on a long
  /// stderr blob and turn a build failure into a 500. Non-empty per
  /// the entity contract (`fail()` requires `errorReason`).
  errorReason: z.string().min(1),
  /// Why the build failed, as the structured code the console classifies on —
  /// never `errorReason`, which carries the tenant's own build output. Read
  /// leniently: a row written before the field existed, or carrying a code this
  /// build does not know, reads as `null` (the generic failure) instead of
  /// failing the row's parse and dropping it from the feed.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// Where the archived log object lives (an `s3://bucket/key` URI),
  /// or EMPTY when there is none.
  ///
  /// This was `.min(1)`, and the only reason that ever held is that a
  /// claim used to fabricate `s3://vibe-logs/<org>/<job>.log` — a path
  /// into a bucket no stack has ever created. The constraint was
  /// satisfied by a string, never by a file. Now that the location is
  /// recorded only once a log has actually been stored, "no stored log"
  /// is a real and common state: a build that printed nothing, or one
  /// whose archive write failed. Rejecting it here would turn that into
  /// a thrown `VibeAuditEvent.create` and lose the audit row entirely —
  /// spending a whole build's audit trail to defend a field that was
  /// lying anyway.
  logsRef: z.string()
});
export type VibeAuditBuildJobFailedPayload = z.infer<typeof VibeAuditBuildJobFailedPayloadSchema>;

/**
 * Emitted when the build-job timeout sweep ends an in-flight build TIMED_OUT.
 * The sweep writes this INSIDE the same `$transaction` as the build-job
 * UPDATE, so the audit row + the TIMED_OUT flip land atomically — neither
 * survives without the other, per
 * `feedback_swallowed_fire_and_forget_silent_failure`. The admin manual
 * escape-hatch (`MarkVibeBuildJobTimedOutUseCase`) does NOT emit this:
 * operator-driven transitions are captured by the admin AuditAction
 * trail instead, mirroring the FAILED / SUCCEEDED slices' split.
 *
 * Two budgets end a build here, and `reasonCode` says which: `BUILD_TIMED_OUT`
 * for a build that had compute and ran past the run budget, and
 * `BUILD_PLACEMENT_TIMED_OUT` for one its executor accepted and never gave
 * compute. `durationMs` is the wallclock the build ran on compute — the run
 * clock the sweep measured — and is aggregated as VIBE_BUILD_MIN; a capacity
 * wait never ran, so it is 0. `errorReason` is the sweep's sentence. `logsRef`
 * is the archived log object, or empty when none was stored.
 */
export const VibeAuditBuildJobTimedOutPayloadSchema = z.object({
  eventType: z.literal("BUILD_JOB_TIMED_OUT"),
  vibeBuildJobId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  /// Wallclock in ms the build ran on compute, as the sweep measured it; 0
  /// for a build that never got compute. Non-negative integer per the entity
  /// invariant (the entity factory throws on negative / non-integer).
  /// `.finite()` is defensive against a future floating-point migration path
  /// landing in audit payloads.
  durationMs: z.number().int().nonnegative().finite(),
  /// Surface text shown by the CLI — the sweep's sentence for the budget it
  /// ran out of. No max length: capping here would make `VibeAuditEvent.create`
  /// throw on a long reason and turn a timeout into a 500. Non-empty per the
  /// entity contract (`timeOut()` always sets a reason).
  errorReason: z.string().min(1),
  /// Which budget ended the build — `BUILD_TIMED_OUT` or
  /// `BUILD_PLACEMENT_TIMED_OUT` — as the structured code the console
  /// classifies on. Read leniently: a row written before the field existed
  /// reads as `null`, which a reader takes as `BUILD_TIMED_OUT`, the only
  /// budget there was then.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// Where the archived log object lives (an `s3://bucket/key` URI),
  /// or EMPTY when there is none.
  ///
  /// This was `.min(1)`, and the only reason that ever held is that a
  /// claim used to fabricate `s3://vibe-logs/<org>/<job>.log` — a path
  /// into a bucket no stack has ever created. The constraint was
  /// satisfied by a string, never by a file. Now that the location is
  /// recorded only once a log has actually been stored, "no stored log"
  /// is a real and common state: a build that printed nothing, or one
  /// whose archive write failed. Rejecting it here would turn that into
  /// a thrown `VibeAuditEvent.create` and lose the audit row entirely —
  /// spending a whole build's audit trail to defend a field that was
  /// lying anyway.
  logsRef: z.string()
});
export type VibeAuditBuildJobTimedOutPayload = z.infer<
  typeof VibeAuditBuildJobTimedOutPayloadSchema
>;

/**
 * Why an attempt of a build ended without an outcome and was queued again.
 *
 *   - LEASE_EXPIRED — the executor stopped renewing the build's heartbeat lease:
 *     nobody is known to be running it any more.
 *   - EXECUTOR_FAULT — the executor reported a fault that is the platform's, not
 *     the build's (the node running it was lost, or the executor lost the build
 *     itself), or refused the dispatch with an error it marked retryable.
 *
 * A build that FAILED — its own code, its own dependencies — is never re-queued.
 */
export const VIBE_BUILD_REQUEUE_CAUSES = ["LEASE_EXPIRED", "EXECUTOR_FAULT"] as const;
export const VibeBuildRequeueCauseSchema = z.enum(VIBE_BUILD_REQUEUE_CAUSES);
export type VibeBuildRequeueCause = z.infer<typeof VibeBuildRequeueCauseSchema>;

/**
 * Emitted when an attempt of a build ended without an outcome and the SAME
 * build job was queued again with `attempt + 1` — the one automatic retry.
 * Written in the transaction that re-queues the row, so the feed carries exactly
 * one row per retry.
 *
 * No duration and no usage event: an attempt that ended this way produced no
 * result the tenant can use, and whether it ran at all is exactly what nobody
 * knows.
 */
export const VibeAuditBuildJobRequeuedPayloadSchema = z.object({
  eventType: z.literal("BUILD_JOB_REQUEUED"),
  vibeBuildJobId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  /// The attempt that ended, and the one now queued (always `fromAttempt + 1`).
  fromAttempt: z.number().int().positive(),
  toAttempt: z.number().int().positive(),
  cause: VibeBuildRequeueCauseSchema,
  /// The executor's code for an EXECUTOR_FAULT — one `isVibeExecutorBuildFault`
  /// accepts: `BUILD_NODE_LOST`, `BUILD_LOST_BY_EXECUTOR`, `BUILD_EXECUTOR_FAULT`
  /// or `BUILD_EXECUTOR_QUEUE_TIMED_OUT`; null for a lapsed lease, which no
  /// executor reported. Read leniently, like every stored code.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// The executor's own name for the attempt that ended, when it gave one.
  executorRef: z.string().nullable()
});
export type VibeAuditBuildJobRequeuedPayload = z.infer<
  typeof VibeAuditBuildJobRequeuedPayloadSchema
>;

/**
 * Emitted when a build job's heartbeat lease lapsed on its LAST allowed attempt:
 * the row goes terminal LOST, in the same transaction as this row and as its
 * deployment's DEPLOYMENT_FAILED. LOST is not FAILED — nothing about the build
 * is known to be wrong; the executor stopped answering for it.
 *
 * `durationMs` is the wallclock between the attempt's start and its last
 * renewal: the stretch anyone knows it was alive. It is recorded, and it is not
 * metered — a lost attempt is the platform's loss.
 */
export const VibeAuditBuildJobLostPayloadSchema = z.object({
  eventType: z.literal("BUILD_JOB_LOST"),
  vibeBuildJobId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  attempt: z.number().int().positive(),
  durationMs: z.number().int().nonnegative().finite(),
  errorReason: z.string().min(1),
  reasonCode: VibeFailureReasonCodeReadSchema,
  executorRef: z.string().nullable(),
  /// Where the archived log object lives, or EMPTY when none was stored — the
  /// same contract as BUILD_JOB_TIMED_OUT's.
  logsRef: z.string()
});
export type VibeAuditBuildJobLostPayload = z.infer<typeof VibeAuditBuildJobLostPayloadSchema>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
/** `BUILD_JOB_REQUEUED.cause`, read leniently. Declared here, beside the list it reads. */
export const VibeAuditBuildRequeueCauseReadSchema = enumReadSchema(VIBE_BUILD_REQUEUE_CAUSES);

export const VibeAuditBuildJobRequeuedReadPayloadSchema =
  VibeAuditBuildJobRequeuedPayloadSchema.extend({
    cause: VibeAuditBuildRequeueCauseReadSchema
  });
